import React, { useEffect, useState, useRef } from "react";
import { FaCheckCircle, FaChevronRight, FaShieldAlt } from "react-icons/fa";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import HeaderWithBack from "../../../components/HeaderWithBack";
import { useCheckout } from "../../../hooks/useCheckout";
import { usePayment } from "../../../hooks/usePayment";
import { useAuthStore } from "../../../stores/authStores";
import { useCheckoutStore } from "../../../stores/checkoutStore";
import { formatRupiah } from "../../../utils/formatPrice";
import FullscreenLoading from "../../Prescription/Components/FullScreenLoader";
import { BenefitBadge } from "../../../components/card/BenefitBadgeCard";
import { isAxiosError } from 'axios';
import axios from '../../../api/axios';

const CheckoutTeleconsultation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { teleconsultationId } = useParams<{ teleconsultationId: string }>();
  const userData = useAuthStore((state) => state.user);
  const authToken = useAuthStore((state) => state.token);
  const boundTeleconsultationId = useCheckoutStore((s) => s.teleconsultationId);
  const isCheckoutBound = !!teleconsultationId && boundTeleconsultationId === teleconsultationId;
  const storedPayment = useCheckoutStore((s) => s.selectedPayment);
  const storedVoucher = useCheckoutStore((s) => s.selectedVoucher);
  const selectedPayment = isCheckoutBound ? storedPayment : null;
  const selectedVoucher = isCheckoutBound ? storedVoucher : null;
  const setPayment = useCheckoutStore((s) => s.setPayment);
  const setVoucher = useCheckoutStore((s) => s.setVoucher);
  const bindTeleconsultation = useCheckoutStore((s) => s.bindTeleconsultation);

  React.useLayoutEffect(() => {
    if (teleconsultationId) bindTeleconsultation(teleconsultationId);
  }, [teleconsultationId, bindTeleconsultation]);

  const [paymentTimeLeft, setPaymentTimeLeft] = useState<number | null>(null);
  const paymentTimerRef = useRef<NodeJS.Timeout | null>(null);

  // ─── Popup "waktu habis" ──────────────────────────────────────────────
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [pendingPaymentUrl, setPendingPaymentUrl] = useState<string | null>(null);
  const [canCreatePayment, setCanCreatePayment] = useState(true);
  const verifyTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasHandledExpiry = useRef(false);

  const formatTime = (seconds: number): string => {
    const safeSeconds = Math.max(0, Math.floor(seconds));
    const mins = Math.floor(safeSeconds / 60).toString().padStart(2, "0");
    const secs = (safeSeconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  const [toast, setToast] = useState<{ message: string; type: "error" | "success" } | null>(null);
  const isAuto = location.state?.isAuto === true;
  const { data: checkoutInfo, isLoading, error: checkoutError } = useCheckout({
    authToken,
    teleconsultationId,
    selectedVoucher,
    selectedPayment,
  });

  // ─── FIX UTAMA: cache checkoutInfo terakhir yang sukses di ref ─────────
  // useCheckout() fetch ULANG setiap kali komponen ini mount (yaitu setiap
  // kali user balik dari halaman /payment-method atau /voucher, karena itu
  // route terpisah -> unmount/mount, BUKAN bug, itu perilaku normal router).
  //
  // Selama fetch baru berjalan, `isLoading` jadi true & `checkoutInfo`
  // sempat undefined sebentar -- kalau langsung return <FullscreenLoading />,
  // SELURUH layar checkout (rincian pesanan, voucher, dst yang SEBENARNYA
  // sudah pernah berhasil di-load) hilang sebentar lalu muncul lagi.
  // ITULAH yang terlihat seperti "balik ke tampilan awal" / flicker.
  //
  // Dengan cache di ref ini, begitu pernah berhasil load SATU KALI, mount
  // berikutnya tetap pakai data lama dulu (sambil di belakang layar fetch
  // ulang utk data terbaru) -- tidak ada lagi flicker ke full-screen loading
  // untuk transisi bolak-balik halaman yang singkat.
  const lastGoodCheckoutInfoRef = useRef<typeof checkoutInfo | null>(null);
  if (checkoutInfo) {
    lastGoodCheckoutInfoRef.current = checkoutInfo;
  }
  const displayedCheckoutInfo = checkoutInfo ?? lastGoodCheckoutInfoRef.current;
  // Loading penuh HANYA untuk kunjungan pertama kali (belum pernah ada
  // data checkout sama sekali, mis. fresh page load).
  const isFirstLoad = isLoading && !displayedCheckoutInfo;

  const { paying, createPayment } = usePayment({
    authToken,
    navigate,
  });
  const payingRef = useRef(paying);
  payingRef.current = paying;
  

  const showToast = (message: string, type: "error" | "success" = "error") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    const response = (checkoutError as any)?.response;
    if (response?.status === 422 && response?.data?.code === 'VOUCHER_UNAVAILABLE' && selectedVoucher) {
      setVoucher(null);
      showToast(`${response.data.message || 'Voucher tidak dapat digunakan'}. Voucher dilepas dari pesanan.`);
    }
  }, [checkoutError]);

  const serviceType = location.state?.serviceType || "chat";
  const getPertanyaan = (): any => {
    if (location.state?.pertanyaan) return location.state.pertanyaan;
    const saved = localStorage.getItem("teleconsult_disclaimer");
    if (saved) { try { return JSON.parse(saved); } catch {} }
    return { berat: "", tinggi: "", lamaSakit: "", minumObat: "", riwayatCovid: "" };
  };
  const pertanyaan = getPertanyaan();
  

  useEffect(() => {
    if (location.state?.selectedVoucher) {
      setVoucher(location.state.selectedVoucher);
    }
    if (location.state?.selectedPayment) {
      setPayment(location.state.selectedPayment);
    }
  }, []);

  const syncPaymentDeadline = React.useCallback(async () => {
    if (!teleconsultationId) return;
    try {
      const res = await axios.get(`teleconsult/${teleconsultationId}/payment-deadline`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = res.data || {};

      if (data.is_post_payment) {
        navigate(`/connect-doctor/${teleconsultationId}?stage=final-confirm`, { replace: true });
        return;
      }

      if (data.phase === 'ended') {
        if (data.status === 'expired' && !hasHandledExpiry.current) {
          hasHandledExpiry.current = true;
          setShowExpiredModal(true);
          return;
        }
        navigate('/home', { replace: true });
        return;
      }

      if (verifyTimerRef.current) clearTimeout(verifyTimerRef.current);

      if (data.verifying) {
        setVerifyingPayment(true);
        setPaymentTimeLeft(null);
        verifyTimerRef.current = setTimeout(() => syncPaymentDeadlineRef.current(), 5000);
        return;
      }

      setVerifyingPayment(false);
      setPendingPaymentUrl(data.pending_payment_url || null);
      setCanCreatePayment(data.can_create_payment !== false);

      const seconds = Math.max(0, Math.floor(Number(data.time) || 0));
      if (data.expired === true || seconds <= 0) {
        setPaymentTimeLeft(0);
        return;
      }

      setPaymentTimeLeft(seconds);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 404) {
        navigate('/home', { replace: true });
        return;
      }
      console.error("Gagal mengambil payment deadline");
    }
  }, [authToken, teleconsultationId, navigate]);

  const syncPaymentDeadlineRef = useRef(syncPaymentDeadline);
  syncPaymentDeadlineRef.current = syncPaymentDeadline;

  const expirePayment = React.useCallback(async () => {
    if (hasHandledExpiry.current || payingRef.current) return;
    hasHandledExpiry.current = true;

    try {
      const res = await axios.post(
        `teleconsult/${teleconsultationId}/cancel-by-system`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (res.data?.phase && res.data.phase !== 'ended') {
        hasHandledExpiry.current = false;
        syncPaymentDeadline();
        return;
      }
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        hasHandledExpiry.current = false;
        syncPaymentDeadline();
        return;
      }
    }

    setShowExpiredModal(true);
  }, [authToken, teleconsultationId, syncPaymentDeadline]);

  useEffect(() => {
    if (paymentTimeLeft === null) return;

    if (paymentTimeLeft <= 0) {
      expirePayment();
      return;
    }

    paymentTimerRef.current = setTimeout(() => {
      setPaymentTimeLeft((prev) => (prev === null ? null : Math.max(0, prev - 1)));
    }, 1000);

    return () => {
      if (paymentTimerRef.current) clearTimeout(paymentTimerRef.current);
    };
  }, [paymentTimeLeft, expirePayment]);

  useEffect(() => {
    syncPaymentDeadline();

    const onVisible = () => {
      if (document.visibilityState === 'visible') syncPaymentDeadline();
    };
    const onPageShow = () => syncPaymentDeadline();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('focus', onPageShow);
    const resyncInterval = setInterval(syncPaymentDeadline, 30000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('focus', onPageShow);
      clearInterval(resyncInterval);
      if (verifyTimerRef.current) clearTimeout(verifyTimerRef.current);
    };
  }, [syncPaymentDeadline]);

  useEffect(() => {
    return () => {
      if (paymentTimerRef.current) clearTimeout(paymentTimerRef.current);
    };
  }, []);

  // Loading penuh HANYA untuk kunjungan pertama kali. Untuk mount ulang
  // setelah balik dari halaman lain, tetap render checkout pakai data
  // terakhir yang berhasil, supaya tidak terasa seperti "reset ke awal".
  if (isFirstLoad || !displayedCheckoutInfo) {
    return <FullscreenLoading />;
  }

  const checkoutInfoToRender = displayedCheckoutInfo;

  const consultationPrice =
    serviceType === "video"
      ? checkoutInfoToRender.doctor.price_video
      : checkoutInfoToRender.doctor.price_chat;

  const transactionFee  = checkoutInfoToRender.transaction_fee;
  const subtotal = consultationPrice + transactionFee;
  const voucherDiscount = checkoutInfoToRender?.discount ?? 0;
  const benefitInfo     = checkoutInfoToRender.benefit;
  const totalAfterDiscount = checkoutInfoToRender.total;
  const isFree = totalAfterDiscount === 0;
  const isCoveredByBenefit = benefitInfo?.eligible && (benefitInfo?.ditanggung_asuransi ?? 0) >= subtotal;
  const showPayment = totalAfterDiscount > 0;
  const showVoucher = !isCoveredByBenefit;
  const insurancePercent = Math.round((benefitInfo?.persentase_cover_asuransi ?? 0) * 100);
  const insuranceAmount = benefitInfo?.ditanggung_asuransi ?? 0;

  const openPaymentPage = () => {
    const encToken = localStorage.getItem(`teleconsult_token_${teleconsultationId}`) || '';
    navigate(`/payment-success?teleconsult_id=${teleconsultationId}&enc_token=${encToken}`);
  };

  const handleBackToHome = () => {
    navigate("/home", { state: { reason: "payment-expired" } });
  };

  const handleSubmitPayment = async () => {
    if (!isFree && !selectedPayment) { 
      showToast("Pilih metode pembayaran dulu"); 
      return; 
    }
    if (!pertanyaan.berat || !pertanyaan.tinggi) {
      showToast("Data berat dan tinggi badan belum lengkap.");
      return;
    }

    try {
      const payload: any = {
        user_id: userData.id,
        pertanyaan,
        voucher_id: selectedVoucher?.id || null,
        total_amount: totalAfterDiscount,
        teleconsultation_id: teleconsultationId,   
      };

      if (!isFree) {
        payload.payment_method_id = selectedPayment?.id;
      }      
      await createPayment({
        teleconsultId: teleconsultationId,   
        payload,
        checkoutInfo: checkoutInfoToRender,
        onVoucherChanged: () => {
          showToast("Voucher berubah");
        },
      });
    } catch (err: any) {
      const code = err.response?.data?.code;

      if (err.response?.status === 410 || code === 'PAYMENT_EXPIRED') {
        hasHandledExpiry.current = true;
        setShowExpiredModal(true);
        return;
      }

      if (code === 'VOUCHER_UNAVAILABLE') {
        setVoucher(null);
        showToast(err.response?.data?.message || "Voucher tidak dapat digunakan");
        return;
      }

      if (code === 'PAYMENT_WINDOW_CLOSED') {
        showToast(err.response?.data?.message || "Silakan selesaikan pembayaran yang sudah dibuat");
        openPaymentPage();
        return;
      }

      showToast(err.response?.data?.message || "Payment gagal");

      if (paymentTimeLeft === 0) {
        setTimeout(expirePayment, 0);
      }
    }
  };
  

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col pb-32">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999]">
          <div
            className={`flex items-start gap-3 px-5 py-4 rounded-xl shadow-lg text-white text-sm
              max-w-sm w-[90vw] animate-slideDown
              ${toast.type === "error" ? "bg-red-500" : "bg-teal-500"}`}
          >
            <span className="text-lg">{toast.type === "error" ? "❌" : "✅"}</span>
            <div className="flex-1">
              <p className="font-semibold">{toast.type === "error" ? "Gagal" : "Berhasil"}</p>
              <p className="opacity-90">{toast.message}</p>
            </div>
            <button onClick={() => setToast(null)} className="text-white opacity-70 hover:opacity-100 text-lg">×</button>
          </div>
        </div>
      )}

      <HeaderWithBack title="Pembayaran" url="/home" />

      {/* Indikator kecil saat refetch di belakang layar (BUKAN full-screen) */}
      {isLoading && displayedCheckoutInfo && (
        <div className="w-full bg-gray-50 py-1.5 text-center">
          <p className="text-xs text-gray-400">Memperbarui data...</p>
        </div>
      )}

      {/* ───────────────── Popup waktu habis ───────────────── */}
      {showExpiredModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[9999] px-5">
          <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-xl text-center">
            <div className="flex flex-col items-center px-6 pt-7 pb-5">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                <span className="text-amber-500 text-3xl">⏰</span>
              </div>

              <h2 className="mt-4 text-lg font-bold text-gray-900">
                Waktu Pembayaran Habis
              </h2>

              <p className="text-sm text-gray-500 mt-2 leading-relaxed">
                Waktu pembayaran untuk konsultasi ini sudah habis dan konsultasi
                telah dibatalkan secara otomatis. Silakan ajukan permintaan
                konsultasi baru untuk melanjutkan.
              </p>
            </div>

            <div className="flex border-t border-gray-100">
              <button
                onClick={handleBackToHome}
                className="flex-1 py-3.5 text-gray-600 text-sm font-medium border-r border-gray-100"
              >
                Kembali ke Beranda
              </button>
            </div>
          </div>
        </div>
      )}

      {verifyingPayment && !showExpiredModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[9999] px-5">
          <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-xl text-center px-6 py-7">
            <img src="/logo-linksehat.png" alt="Loading" className="w-14 h-14 mx-auto animate-pulse" />
            <h2 className="mt-4 text-lg font-bold text-gray-900">Memverifikasi Pembayaran</h2>
            <p className="text-sm text-gray-500 mt-2 leading-relaxed">
              Waktu pembayaran sudah habis. Kami sedang memastikan apakah pembayaran Anda sudah diterima.
              Mohon tunggu sebentar.
            </p>
          </div>
        </div>
      )}

      {paymentTimeLeft !== null && !showExpiredModal && (
        <div className="w-full bg-yellow-100 py-3 text-center">
          <p className="text-sm text-amber-600">
            {pendingPaymentUrl ? 'Selesaikan pembayaran di Duitku dalam' : 'Selesaikan pembayaran dalam'}
          </p>
          <p className="text-xl font-bold text-amber-600 mt-0.5">
            {formatTime(paymentTimeLeft)}
          </p>
          {/* {!pendingPaymentUrl && (
            <p className="text-xs text-amber-600 mt-1">
              Saat membayar, waktu di halaman Duitku dibulatkan ke atas per menit.
            </p>
          )} */}
          {pendingPaymentUrl && (
            <button
              onClick={openPaymentPage}
              className="mt-2 px-4 py-1.5 rounded-full bg-amber-500 text-white text-xs font-semibold"
            >
              Lanjutkan Pembayaran
            </button>
          )}
        </div>
      )}

      {/* Doctor Card */}
      <div className="px-4 mt-4">
        <div className="bg-white rounded-xl shadow p-4 flex items-center gap-3">
          <img src={checkoutInfoToRender.doctor.photoUrl} alt="Doctor" className="w-12 h-12 rounded-full object-cover" />
          <div className="flex-1">
            <p className="font-semibold text-gray-800">{checkoutInfoToRender.doctor.name}</p>
            <p className="text-sm text-gray-500">{checkoutInfoToRender.doctor.specialization}</p>
            {isAuto && <p className="text-xs text-teal-600 mt-1">✨ Dokter akan dipilihkan otomatis</p>}
          </div>
        </div>

        <div className="mt-3 bg-pink-100 text-pink-700 rounded-lg px-3 py-2 flex items-center gap-2 text-sm">
          <FaCheckCircle />
          <span>Kami menjamin privasi anda saat berkonsultasi</span>
        </div>

        {/* ── BENEFIT BADGE ── */}
        {benefitInfo && (
          <BenefitBadge benefit={benefitInfo} consultationPrice={consultationPrice} />
        )}
      </div>

      {/* Rincian Pesanan */}
      <div className="px-4 mt-4">
        <div className="bg-white rounded-xl shadow p-4">
          <p className="font-semibold text-gray-800 mb-3">Rincian Pesanan</p>

          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-600">Konsultasi ({serviceType})</span>
            <span>{formatRupiah(consultationPrice)}</span>
          </div>

          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-600">Biaya Transaksi</span>
            <span>{formatRupiah(transactionFee)}</span>
          </div>

          {selectedVoucher && (
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Voucher</span>
              <span className="text-teal-600">- {formatRupiah(voucherDiscount)}</span>
            </div>
          )}

          {/* Benefit row */}
          {benefitInfo?.eligible && (
            <div className="flex justify-between text-sm mb-2 items-center">
              <span className="flex items-center gap-1.5 text-gray-600">
                <FaShieldAlt className="text-emerald-500 text-xs" />
                Benefit Asuransi
              </span>
              <span className="text-emerald-600 font-medium">
                {insurancePercent}% Ditanggung Asuransi <br />
                <span className="text-xs">
                  - {formatRupiah(insuranceAmount)}
                </span>
              </span>
            </div>
          )}

          <hr className="my-2" />

          <div className="flex justify-between font-semibold">
            <span>Total</span>
            <div className="text-right">
              {(voucherDiscount > 0 || benefitInfo?.eligible) && (
                <p className="text-xs text-gray-400 line-through">{formatRupiah(subtotal)}</p>
              )}
              <span className="text-teal-600">{formatRupiah(totalAfterDiscount)}</span>
            </div>
          </div>

          {/* Hemat banner */}
          {(voucherDiscount > 0 || benefitInfo?.eligible) && (
            <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex items-center gap-2">
              <span className="text-emerald-600 text-lg">🎉</span>
              <span className="text-emerald-700 text-xs font-medium">
                Kamu hemat{" "}
                {formatRupiah(
                  benefitInfo?.eligible ? subtotal : voucherDiscount
                )}{" "}
                dari total tagihan!
              </span>
            </div>
          )}
        </div>
      </div>

      {!canCreatePayment && !showExpiredModal && (
        <div className="px-4 mt-4">
          <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-lg px-3 py-2">
            {pendingPaymentUrl
              ? 'Sisa waktu kurang dari 1 menit sehingga metode pembayaran tidak dapat diganti. Selesaikan pembayaran dengan metode yang sudah dipilih.'
              : 'Sisa waktu pembayaran kurang dari 1 menit sehingga pembayaran baru tidak dapat dibuat.'}
          </div>
        </div>
      )}

      {showPayment && canCreatePayment && (
        <div className="px-4 mt-4">
          <p className="text-md font-bold mb-2">Metode Pembayaran</p>
          <div
            onClick={() =>
              navigate(`/payment-method/${teleconsultationId}`, {
                state: { isAuto, selectedPayment, selectedVoucher, subtotal, pertanyaan },
              })
            }
            className="bg-white rounded-xl shadow p-4 flex justify-between items-center cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <img
                src={selectedPayment?.displayIcon || "/app-icons/payment.svg"}
                alt="payment"
                className="w-8 h-8 object-contain"
              />
              <p className="font-medium">
                {selectedPayment ? selectedPayment.displayName : "Pilih Metode Pembayaran"}
              </p>
            </div>
            <FaChevronRight />
          </div>
        </div>
      )}

      {showVoucher && canCreatePayment && (
          <div className="px-4 mt-4">
            <p className="text-md font-bold mb-2">Voucher</p>
            <div
              onClick={() =>
                navigate(`/voucher/${teleconsultationId}`, {
                  state: { isAuto, selectedPayment, selectedVoucher, pertanyaan, subtotal },
                })
              }
              className="bg-white rounded-xl shadow p-4 flex justify-between items-center cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <img src="/app-icons/voucher.svg" className="w-8 h-8 object-contain" alt="voucher" />
                <div>
                  <p className="font-medium">{selectedVoucher ? selectedVoucher.code : "Gunakan Voucher"}</p>
                  {selectedVoucher && (
                    <p className="text-sm text-gray-500">{formatRupiah(voucherDiscount)}</p>
                  )}
                </div>
              </div>
              <FaChevronRight />
            </div>
          </div>
      )}

      {/* Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow px-4 py-3 flex justify-between items-center">
        <div>
          <p className="text-xs text-gray-500">Total Bayar</p>
          <p className="text-teal-600 font-bold text-lg">{formatRupiah(totalAfterDiscount)}</p>
          {benefitInfo?.eligible && (
            <div className="text-xs text-teal-600 mt-2">
              Asuransi akan diproses setelah pembayaran berhasil
            </div>
          )}
        </div>
        <button
          onClick={!canCreatePayment && pendingPaymentUrl ? openPaymentPage : handleSubmitPayment}
          disabled={paying || showExpiredModal || (!canCreatePayment && !pendingPaymentUrl)}
          className="bg-teal-500 text-white px-6 py-2.5 rounded-xl font-semibold disabled:opacity-50 flex items-center gap-2"
        >
          {!canCreatePayment && pendingPaymentUrl
            ? "Lanjutkan Pembayaran"
            : isFree ? "Mulai Konsultasi" : "Bayar Sekarang"}
        </button>
      </div>
    </div>
  );
};

export default CheckoutTeleconsultation;
